import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import moment from 'moment-timezone';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { IVolunteerShift, IVolunteerShiftRole } from '../../../queries/Volunteer';
import { Button } from '../../components-v2/Button';
import { DateInput } from '../../components-v2/DateInput';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../components-v2/Drawer';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel } from '../../components-v2/Field';
import { Input } from '../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components-v2/Select';
import { TabToggle } from '../../components-v2/TabToggle';
import { TextArea } from '../../components-v2/TextArea';
import { THEME } from '../../components-v2/Theme/theme';
import { TimeInput, normalizeTimeInput } from '../../components-v2/TimeInput';
import { JobPicker } from './JobPicker';
import { composeShiftInstant, resolveZone, sliceTimeWindow } from './shiftScheduling';

type Mode = 'single' | 'generate';

export interface IBuildShiftBodiesInput {
	mode: Mode;
	date: string;
	startTime: string;
	endTime: string;
	durationMinutes: number | null;
	location: string;
	description: string;
	capacity: number | null;
	roles: IVolunteerShiftRole[];
	zone?: string;
}

const DURATION_OPTIONS: { value: string; label: string }[] = [
	{ value: '30', label: '30 minutes' },
	{ value: '60', label: '1 hour' },
	{ value: '90', label: '1.5 hours' },
	{ value: '120', label: '2 hours' },
	{ value: '180', label: '3 hours' },
	{ value: '240', label: '4 hours' },
];

export const buildShiftBodies = (input: IBuildShiftBodiesInput): Partial<IVolunteerShift>[] => {
	if (!input.date) {
		return [];
	}
	const slots =
		input.mode === 'single'
			? [{ start: input.startTime, end: input.endTime }]
			: input.durationMinutes
				? sliceTimeWindow(input.startTime, input.endTime, input.durationMinutes)
				: [];
	return slots.map(slot => ({
		startAt: composeShiftInstant(input.date, slot.start, input.zone),
		endAt: composeShiftInstant(input.date, slot.end, input.zone),
		location: input.location.trim() || undefined,
		description: input.description.trim() || undefined,
		// Shift-level capacity governs only when the shift lists no jobs; otherwise each job's headcount does.
		capacity: input.roles.length ? undefined : (input.capacity ?? undefined),
		roles: input.roles,
	}));
};

export interface IActivityDateRange {
	start?: string;
	end?: string;
}

interface IProps {
	isOpen: boolean;
	onClose: () => void;
	activityTimeZone?: string;
	activityDateRange?: IActivityDateRange;
	shift?: IVolunteerShift;
	createShifts: (
		bodies: Partial<IVolunteerShift>[]
	) => Promise<{ created: number; failed: number; succeeded: Partial<IVolunteerShift>[] }>;
	updateShift: (shift: IVolunteerShift) => Promise<void>;
	impersonationContext?: Api.IImpersonationContext;
}

export const outOfActivityRangeWarning = (date: string, range: IActivityDateRange | undefined): string | undefined => {
	if (!date) {
		return undefined;
	}
	if (range?.start && date < range.start) {
		return `This date is before the activity's start date (${moment(range.start, 'YYYY-MM-DD').format('MMM D, YYYY')}).`;
	}
	if (range?.end && date > range.end) {
		return `This date is after the activity's end date (${moment(range.end, 'YYYY-MM-DD').format('MMM D, YYYY')}).`;
	}
	return undefined;
};

export const timesValid = (startTime: string, endTime: string): boolean => {
	const start = normalizeTimeInput(startTime);
	const end = normalizeTimeInput(endTime);
	return !!start && !!end && start < end;
};

export const shiftBodyKey = (body: Partial<IVolunteerShift>): string => `${body.startAt ?? ''}|${body.endAt ?? ''}`;

export const ShiftFormDrawer = ({
	isOpen,
	onClose,
	activityTimeZone,
	activityDateRange,
	shift,
	createShifts,
	updateShift,
	impersonationContext,
}: IProps) => {
	const isEditing = !!shift;
	const [mode, setMode] = useState<Mode>('single');

	const zone = resolveZone(activityTimeZone);
	const [date, setDate] = useState(shift?.startAt ? moment.tz(shift.startAt, zone).format('YYYY-MM-DD') : '');
	const [duration, setDuration] = useState('');
	const [startTime, setStartTime] = useState(shift?.startAt ? moment.tz(shift.startAt, zone).format('HH:mm') : '');
	const [endTime, setEndTime] = useState(shift?.endAt ? moment.tz(shift.endAt, zone).format('HH:mm') : '');
	const [location, setLocation] = useState(shift?.location ?? '');
	const [description, setDescription] = useState(shift?.description ?? '');
	const [capacity, setCapacity] = useState(shift?.capacity != null ? String(shift.capacity) : '');
	const [roles, setRoles] = useState<IVolunteerShiftRole[]>(shift?.roles ?? []);
	const [error, setError] = useState<string | undefined>();

	const [isSaving, setIsSaving] = useState(false);
	const createdKeys = useRef(new Set<string>());

	const dateRangeWarning = outOfActivityRangeWarning(date, activityDateRange);
	const parsedCapacity = capacity.trim() ? Number(capacity) : null;
	const durationMinutes = duration ? Number(duration) : null;
	const bodies = buildShiftBodies({
		mode,
		date,
		startTime,
		endTime,
		durationMinutes,
		location,
		description,
		capacity: parsedCapacity,
		roles,
		zone: activityTimeZone,
	});

	const validate = (): string | undefined => {
		if (roles.length === 0 && parsedCapacity != null && (!Number.isInteger(parsedCapacity) || parsedCapacity < 1)) {
			return 'Capacity must be a whole number of at least 1.';
		}
		if (roles.some(role => role.capacity != null && (!Number.isInteger(role.capacity) || role.capacity < 1))) {
			return 'Each job headcount must be a whole number of at least 1.';
		}
		if (!timesValid(startTime, endTime)) {
			return 'Enter a start and end time, with the end after the start.';
		}
		if (!date) {
			return 'Pick a date for the shift.';
		}
		if (mode === 'generate') {
			if (!durationMinutes) {
				return 'Pick a shift duration.';
			}
			if (bodies.length === 0) {
				return 'The time window must fit at least one shift.';
			}
		}
		return undefined;
	};

	const handleSave = async () => {
		const validationError = validate();
		setError(validationError);
		if (validationError) {
			return;
		}
		setIsSaving(true);
		try {
			if (isEditing && shift) {
				try {
					await updateShift({ ...shift, ...bodies[0], id: shift.id });
				} catch {
					return;
				}
				toast.success('Shift saved');
				onClose();
				return;
			}
			const pending = bodies.filter(body => !createdKeys.current.has(shiftBodyKey(body)));
			const { created, failed, succeeded } = await createShifts(pending);
			for (const body of succeeded) {
				createdKeys.current.add(shiftBodyKey(body));
			}
			if (failed === 0) {
				toast.success(bodies.length === 1 ? 'Shift created' : `Created ${bodies.length} shifts`);
				onClose();
			} else {
				toast.error(
					`Created ${created} of ${pending.length} shifts (${failed} failed) — Save retries only the failed ones`
				);
			}
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Drawer
			open={isOpen}
			onOpenChange={next => {
				if (!next) {
					onClose();
				}
			}}
			swipeDirection='right'
		>
			<DrawerContent>
				<DrawerHeader>
					<DrawerTitle>{isEditing ? 'Edit shift' : 'Add shifts'}</DrawerTitle>
				</DrawerHeader>
				<div className={css(styles.body)}>
					{!isEditing ? (
						<TabToggle
							styles={[styles.modeToggle]}
							value={mode}
							onValueChange={next => setMode(next as Mode)}
							items={[
								{ value: 'single', label: 'Single shift' },
								{ value: 'generate', label: 'Multiple shifts' },
							]}
						/>
					) : null}

					<Field>
						<FieldLabel>Date</FieldLabel>
						<FieldContent>
							<DateInput value={date} onChange={setDate} />
						</FieldContent>
					</Field>

					{dateRangeWarning ? <div className={css(styles.warningBanner)}>{dateRangeWarning}</div> : null}

					{mode === 'generate' ? (
						<Field>
							<FieldLabel>Shift duration</FieldLabel>
							<FieldContent>
								<Select value={duration} onValueChange={setDuration}>
									<SelectTrigger>
										<span>
											{DURATION_OPTIONS.find(option => option.value === duration)?.label ?? 'Select duration'}
										</span>
									</SelectTrigger>
									<SelectContent>
										{DURATION_OPTIONS.map(option => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FieldContent>
							<FieldDescription>Back-to-back shifts of this length fill the time window below.</FieldDescription>
						</Field>
					) : null}

					<div className={css(styles.row)}>
						<Field styles={[styles.grow]}>
							<FieldLabel>Start time</FieldLabel>
							<FieldContent>
								<TimeInput value={startTime} onChange={setStartTime} />
							</FieldContent>
						</Field>
						<Field styles={[styles.grow]}>
							<FieldLabel>End time</FieldLabel>
							<FieldContent>
								<TimeInput value={endTime} onChange={setEndTime} minTime={startTime || undefined} />
							</FieldContent>
						</Field>
					</div>

					<Field>
						<FieldLabel>Location</FieldLabel>
						<FieldContent>
							<Input value={location} onChange={e => setLocation(e.target.value)} placeholder='e.g. Main St Shelter' />
						</FieldContent>
					</Field>

					<Field>
						<FieldLabel>Description</FieldLabel>
						<FieldContent>
							<TextArea
								value={description}
								onChange={e => setDescription(e.target.value)}
								rows={3}
								styles={[styles.descriptionTextArea]}
							/>
						</FieldContent>
					</Field>

					<Field>
						<FieldLabel>Job(s)</FieldLabel>
						<FieldContent>
							<JobPicker value={roles} onChange={setRoles} impersonationContext={impersonationContext} />
						</FieldContent>
						<FieldDescription>Give each job its own headcount, or leave it blank for unlimited.</FieldDescription>
					</Field>

					{roles.length === 0 ? (
						<Field>
							<FieldLabel>Capacity</FieldLabel>
							<FieldContent>
								<Input
									type='number'
									min={1}
									value={capacity}
									onChange={e => setCapacity(e.target.value)}
									placeholder='Unlimited'
								/>
							</FieldContent>
							<FieldDescription>Total places for the shift when it isn’t split into jobs.</FieldDescription>
						</Field>
					) : null}

					{mode === 'generate' && bodies.length > 0 ? (
						<div className={css(styles.count)}>Creates {bodies.length} shifts</div>
					) : null}

					<FieldError errors={error ? [{ message: error }] : undefined} />

					<div className={css(styles.actions)}>
						<Button type='button' onClick={handleSave} isLoading={isSaving}>
							{isEditing ? 'Save shift' : mode === 'single' ? 'Add shift' : 'Create shifts'}
						</Button>
						<Button type='button' variant='secondary' onClick={onClose}>
							Cancel
						</Button>
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
};

const styles = StyleSheet.create({
	body: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.md, padding: THEME.spacing.md, maxWidth: 560 },
	modeToggle: { width: '100%' },
	row: { display: 'flex', gap: THEME.spacing.md },
	grow: { flex: 1 },
	count: { color: THEME.colors.text.light, fontSize: THEME.typography.fontSizes.sm },
	warningBanner: {
		backgroundColor: THEME.colors.warning.light,
		borderLeft: `4px solid ${THEME.colors.warning.main}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		padding: THEME.spacing.sm,
	},
	actions: { display: 'flex', gap: THEME.spacing.sm, marginTop: THEME.spacing.sm },
	descriptionTextArea: { backgroundColor: THEME.colors.background.light },
});
